import { ContextConsumer } from '@lit/context'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { cardPartRegistryContext } from './card-context.js'
import style from './parts.css'

/**
 * The footer region of a composed [Card](/components/card/). Typically holds
 * actions or supplementary information.
 *
 * Place it as a direct child of `nord-card`, after
 * [Card Body](/components/card-body/). Set `divider` to draw a separating
 * border above the footer.
 *
 * @status draft
 * @category structure
 * @lightDom true
 *
 * @slot - The card footer content.
 */
@customElement('nord-card-footer')
export default class CardFooter extends DraftComponentMixin(LitElement) {
  /** When set, draws a divider border above the footer. */
  @property({ type: Boolean, reflect: true }) divider = false

  private registry = new ContextConsumer(this, {
    context: cardPartRegistryContext,
    subscribe: true,
    callback: registry => registry?.add(this),
  })

  connectedCallback() {
    super.connectedCallback()
    this.registry.value?.add(this)
    injectLegacyLightDomStyles(this, style, 'nord-card-parts')
  }

  disconnectedCallback() {
    this.registry.value?.remove(this)
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-card-footer': CardFooter
  }
}
